import React from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { Slide, CarouselStyle, FontStyle, Theme, LayoutSettings, SlideNumberPosition } from '@/types';
import { MobileToolbarPanel } from './MobileToolbar';
import SlideListPanel from './panels/SlideListPanel';
import TextEditorPanel from './panels/TextEditorPanel';
import ImagePanel from './panels/ImagePanel';
import StylePanel from './panels/StylePanel';
import ConfigPanel from './panels/ConfigPanel';
import AIPanel from './panels/AIPanel';

interface MobileEditorProps {
  activePanel: MobileToolbarPanel | null;
  onPanelChange: (panel: MobileToolbarPanel | null) => void;

  // Slide list props
  slides: Slide[];
  activeSlideId: string;
  activeSlide: Slide;
  activeIndex: number;
  batchMode: boolean;
  selectedSlideIds: Set<string>;
  generatingSlideIds: Set<string>;
  slideGenerationStatus: Record<string, 'idle' | 'generating' | 'success' | 'error'>;
  batchGenerating: boolean;
  batchGeneratingLabel: string;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  onSlideSelect: (slideId: string) => void;
  onToggleSelection: (slideId: string) => void;
  onDeleteSlide: (slideId: string) => void;
  onDuplicateSlide: (slideId: string) => void;
  onAddSlide: () => void;
  onToggleBatchMode: () => void;
  onToggleSelectAll: () => void;
  onBatchGenerateImages: () => void;
  SortableSlideItem: React.ComponentType<any>;

  // Text editor props
  onUpdateSlides: (slides: Slide[]) => void;
  onTextChange: (text: string) => void;

  // Image props
  style: CarouselStyle;
  layoutSettings: LayoutSettings;
  imageSettingsOpen: boolean;
  setImageSettingsOpen: (open: boolean) => void;
  onToggleImage: (checked: boolean) => void;
  onToggleOverlay: (checked: boolean) => void;
  onGenerateImage: () => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenEditModal: () => void;
  onImageScaleChange: (scale: number) => void;
  onImageOffsetChange: (offset: number) => void;
  onGradientHeightChange: (height: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Style props
  theme: Theme;
  setTheme: (theme: Theme) => void;
  onConvertStyle: (style: CarouselStyle) => void;
  onStyleChange?: (style: CarouselStyle) => void;
  fontStyle: FontStyle;
  setFontStyle: (style: FontStyle) => void;
  showAccent: boolean;
  setShowAccent: (show: boolean) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
  styleOptions: Array<{ value: CarouselStyle; label: string }>;
  fontOptions: Array<{ value: FontStyle; label: string }>;

  // Config props
  showSlideNumbers: boolean;
  setShowSlideNumbers: (show: boolean) => void;
  showVerifiedBadge: boolean;
  setShowVerifiedBadge: (show: boolean) => void;
  slideNumberPosition: SlideNumberPosition;
  setSlideNumberPosition: (position: SlideNumberPosition) => void;
  slideNumberPositionOptions: Array<{ value: SlideNumberPosition; label: string }>;

  // AI props
  globalImageStyle: string;
  setGlobalImageStyle: (style: string) => void;
  defaultImageStyle: string;
  globalFeedback: string;
  setGlobalFeedback: (feedback: string) => void;
  isRefining: boolean;
  onGlobalRefine: () => void;

}

const MobileEditor: React.FC<MobileEditorProps> = (props) => {
  const { activePanel } = props;

  if (!activePanel) return null;

  const renderPanelContent = () => {
    switch (activePanel) {
      case 'slides':
        return (
          <SlideListPanel
            slides={props.slides}
            activeSlideId={props.activeSlideId}
            batchMode={props.batchMode}
            selectedSlideIds={props.selectedSlideIds}
            generatingSlideIds={props.generatingSlideIds}
            slideGenerationStatus={props.slideGenerationStatus}
            batchGenerating={props.batchGenerating}
            batchGeneratingLabel={props.batchGeneratingLabel}
            sensors={props.sensors}
            onDragEnd={props.onDragEnd}
            onSlideSelect={props.onSlideSelect}
            onToggleSelection={props.onToggleSelection}
            onDeleteSlide={props.onDeleteSlide}
            onDuplicateSlide={props.onDuplicateSlide}
            onAddSlide={props.onAddSlide}
            onToggleBatchMode={props.onToggleBatchMode}
            onToggleSelectAll={props.onToggleSelectAll}
            onBatchGenerateImages={props.onBatchGenerateImages}
            SortableSlideItem={props.SortableSlideItem}
          />
        );
      case 'text':
        return (
          <TextEditorPanel
            activeSlide={props.activeSlide}
            activeIndex={props.activeIndex}
            slides={props.slides}
            onUpdateSlides={props.onUpdateSlides}
            onTextChange={props.onTextChange}
          />
        );
      case 'image':
        return (
          <ImagePanel
            activeSlide={props.activeSlide}
            activeIndex={props.activeIndex}
            slides={props.slides}
            style={props.style}
            layoutSettings={props.layoutSettings}
            generatingSlideIds={props.generatingSlideIds}
            imageSettingsOpen={props.imageSettingsOpen}
            setImageSettingsOpen={props.setImageSettingsOpen}
            onUpdateSlides={props.onUpdateSlides}
            onToggleImage={props.onToggleImage}
            onToggleOverlay={props.onToggleOverlay}
            onGenerateImage={props.onGenerateImage}
            onFileUpload={props.onFileUpload}
            onOpenEditModal={props.onOpenEditModal}
            onImageScaleChange={props.onImageScaleChange}
            onImageOffsetChange={props.onImageOffsetChange}
            onGradientHeightChange={props.onGradientHeightChange}
            fileInputRef={props.fileInputRef}
          />
        );
      case 'style':
        return (
          <StylePanel
            theme={props.theme}
            setTheme={props.setTheme}
            style={props.style}
            onConvertStyle={props.onConvertStyle}
            onStyleChange={props.onStyleChange}
            fontStyle={props.fontStyle}
            setFontStyle={props.setFontStyle}
            showAccent={props.showAccent}
            setShowAccent={props.setShowAccent}
            accentColor={props.accentColor}
            setAccentColor={props.setAccentColor}
            styleOptions={props.styleOptions}
            fontOptions={props.fontOptions}
          />
        );
      case 'config':
        return (
          <ConfigPanel
            showSlideNumbers={props.showSlideNumbers}
            setShowSlideNumbers={props.setShowSlideNumbers}
            showVerifiedBadge={props.showVerifiedBadge}
            setShowVerifiedBadge={props.setShowVerifiedBadge}
            slideNumberPosition={props.slideNumberPosition}
            setSlideNumberPosition={props.setSlideNumberPosition}
            slideNumberPositionOptions={props.slideNumberPositionOptions}
          />
        );
      case 'ai':
        return (
          <AIPanel
            globalImageStyle={props.globalImageStyle}
            setGlobalImageStyle={props.setGlobalImageStyle}
            defaultImageStyle={props.defaultImageStyle}
            globalFeedback={props.globalFeedback}
            setGlobalFeedback={props.setGlobalFeedback}
            isRefining={props.isRefining}
            onGlobalRefine={props.onGlobalRefine}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="flex flex-col bg-card border-t border-border rounded-t-2xl overflow-hidden"
      style={{ height: '50vh' }}
    >
      <div className="flex justify-center py-2 flex-shrink-0">
        <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain pb-safe min-h-0">
        {renderPanelContent()}
      </div>
    </div>
  );
};

export default MobileEditor;
